/**
 * CL-DRAW-ORDER-001 / 001A — Canonical drawing ordering.
 *
 * Absent project ordering metadata ⇒ automatic numeric-first natural order (no migration).
 * Manual mode uses pmProjects.drawingOrderMode + pmProjects.drawingFileOrder only.
 *
 * CL-DRAW-ORDER-001A: numeric-leading names sort before alphabetic (no coded-before-uncoded).
 */

export const DRAWING_ORDER_MODES = Object.freeze({
  AUTOMATIC: "automatic",
  MANUAL: "manual",
});

function safeStr(value) {
  try {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  } catch {
    return "";
  }
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

/**
 * Best raw identifier for sorting (displayed values are not rewritten).
 * Prefer filename for leading-character grouping so package/site PDFs stay first.
 * Sheet fields refine alphabetic sheet comparison only.
 */
export function extractDrawingSortSource(file) {
  const f = file || {};
  const name = safeStr(f.name) || safeStr(f.fileName) || safeStr(f.originalFileName) || safeStr(f.originalName);
  const title = safeStr(f.title) || safeStr(f.displayName) || safeStr(f.displayTitle);
  const explicit =
    safeStr(f.sheetNumber) ||
    safeStr(f.sheetCode) ||
    safeStr(f.sheetNo) ||
    safeStr(f.sheet) ||
    safeStr(f.sheet_number);

  const primary = name || title || explicit;
  return {
    source: primary,
    name: name || title || primary,
    sheet: explicit,
    title,
    from: name ? "name" : title ? "title" : explicit ? "sheet" : "name",
  };
}

/**
 * Leading group for numeric-first ordering:
 * 0 = starts with digit, 1 = starts with letter, 2 = other/blank.
 */
export function drawingLeadingGroup(raw) {
  const s = safeStr(raw).replace(/^[\s_\-.]+/, "");
  if (!s) return 2;
  if (/^\d/.test(s)) return 0;
  if (/^[A-Za-z]/.test(s)) return 1;
  return 2;
}

/**
 * Parse a drawing/sheet token into comparable parts.
 * Normalizes A-101 / A 101 / A101; keeps A101A suffix.
 */
export function parseDrawingSheetKey(raw) {
  const original = safeStr(raw);
  if (!original) {
    return {
      coded: false,
      discipline: "",
      number: null,
      suffix: "",
      raw: "",
      normalized: "",
      compareLabel: "",
    };
  }

  const tokenMatch = original.match(
    /([A-Za-z]{1,4})\s*[-_.]?\s*(\d{1,5})\s*([A-Za-z]{0,4})(?=\b|[_\s.\-]|$)/
  );
  if (tokenMatch) {
    const discipline = safeStr(tokenMatch[1]).toUpperCase();
    const number = Number(tokenMatch[2]);
    const suffix = safeStr(tokenMatch[3]).toUpperCase();
    return {
      coded: true,
      discipline,
      number: Number.isFinite(number) ? number : null,
      suffix,
      raw: original,
      normalized: `${discipline}${tokenMatch[2]}${suffix}`,
      compareLabel: original,
    };
  }

  return {
    coded: false,
    discipline: "",
    number: null,
    suffix: "",
    raw: original,
    normalized: original.replace(/[\s_\-.]+/g, "").toLowerCase(),
    compareLabel: original,
  };
}

export function drawingSortKeyForFile(file) {
  const extracted = extractDrawingSortSource(file);
  const groupProbe = extracted.name || extracted.source;
  const sheetProbe = extracted.sheet || extracted.source;
  const parsedFromSheet = parseDrawingSheetKey(sheetProbe);
  const parsedFromName = parseDrawingSheetKey(groupProbe);
  const parsed = parsedFromSheet.coded ? parsedFromSheet : parsedFromName;
  return {
    ...parsed,
    from: extracted.from,
    fileId: safeStr(file?.id),
    fallbackLabel: groupProbe,
    leadingGroup: drawingLeadingGroup(groupProbe),
    naturalLabel: groupProbe.replace(/[\s_\-.]+/g, " ").trim(),
  };
}

function compareNullableNumber(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}

function naturalLabelCompare(a, b) {
  return safeStr(a).localeCompare(safeStr(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/**
 * Numeric-first then alphabetical natural compare (CL-DRAW-ORDER-001A).
 * Does NOT place coded sheets before numeric-leading package/site files.
 */
export function compareDrawingSheetKeys(aKey, bKey) {
  const a = aKey || {};
  const b = bKey || {};

  const g = (a.leadingGroup ?? 2) - (b.leadingGroup ?? 2);
  if (g !== 0) return g;

  // Within alphabetic group, prefer sheet token compare when both coded.
  if ((a.leadingGroup ?? 2) === 1 && a.coded && b.coded) {
    const d = safeStr(a.discipline).localeCompare(safeStr(b.discipline), undefined, {
      sensitivity: "base",
    });
    if (d !== 0) return d;
    const n = compareNullableNumber(a.number, b.number);
    if (n !== 0) return n;
    const s = safeStr(a.suffix).localeCompare(safeStr(b.suffix), undefined, {
      numeric: true,
      sensitivity: "base",
    });
    if (s !== 0) return s;
  }

  const label = naturalLabelCompare(a.naturalLabel || a.fallbackLabel || a.raw, b.naturalLabel || b.fallbackLabel || b.raw);
  if (label !== 0) return label;
  return safeStr(a.fileId).localeCompare(safeStr(b.fileId));
}

export function compareDrawingFiles(a, b) {
  return compareDrawingSheetKeys(drawingSortKeyForFile(a), drawingSortKeyForFile(b));
}

export function normalizeDrawingOrderMode(value) {
  return lower(value) === DRAWING_ORDER_MODES.MANUAL
    ? DRAWING_ORDER_MODES.MANUAL
    : DRAWING_ORDER_MODES.AUTOMATIC;
}

export function normalizeDrawingFileOrder(ids) {
  const out = [];
  const seen = new Set();
  (Array.isArray(ids) ? ids : []).forEach((raw) => {
    const id = safeStr(raw);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

export function readProjectDrawingOrderConfig(project) {
  return {
    mode: normalizeDrawingOrderMode(project?.drawingOrderMode),
    fileOrder: normalizeDrawingFileOrder(project?.drawingFileOrder),
  };
}

export function isArchivedOrDeletedDrawing(file) {
  const status = lower(file?.status);
  if (status === "deleted" || status === "archived") return true;
  if (file?.deleted === true || file?.archived === true) return true;
  return false;
}

/**
 * Automatic natural order. Ignores archived/deleted when dropExcluded is true (default).
 */
export function sortDrawingsAutomatic(files, { includeExcluded = false } = {}) {
  const list = (Array.isArray(files) ? files : []).filter((f) => {
    if (!f) return false;
    if (includeExcluded) return true;
    return !isArchivedOrDeletedDrawing(f);
  });
  return [...list].sort(compareDrawingFiles);
}

export function buildAutomaticDrawingOrderIds(files) {
  return sortDrawingsAutomatic(files).map((f) => safeStr(f.id)).filter(Boolean);
}

/**
 * Apply saved manual file IDs: ignore stale/archived; append unlisted drawings in automatic order.
 */
export function applyManualDrawingOrder(files, fileOrder, { includeExcluded = false } = {}) {
  const list = (Array.isArray(files) ? files : []).filter((f) => {
    if (!f) return false;
    if (includeExcluded) return true;
    return !isArchivedOrDeletedDrawing(f);
  });
  const byId = new Map();
  list.forEach((f) => {
    const id = safeStr(f.id);
    if (id) byId.set(id, f);
  });

  const ordered = [];
  const used = new Set();
  normalizeDrawingFileOrder(fileOrder).forEach((id) => {
    const f = byId.get(id);
    if (!f || used.has(id)) return;
    used.add(id);
    ordered.push(f);
  });

  const remaining = list.filter((f) => !used.has(safeStr(f.id)));
  remaining.sort(compareDrawingFiles);
  return [...ordered, ...remaining];
}

/**
 * Canonical project-aware sort for drawing lists.
 */
export function sortDrawingsCanonical(files, projectOrConfig = null, opts = {}) {
  const cfg =
    projectOrConfig && (projectOrConfig.drawingOrderMode != null || projectOrConfig.drawingFileOrder != null)
      ? readProjectDrawingOrderConfig(projectOrConfig)
      : {
          mode: normalizeDrawingOrderMode(projectOrConfig?.mode),
          fileOrder: normalizeDrawingFileOrder(projectOrConfig?.fileOrder),
        };

  if (cfg.mode === DRAWING_ORDER_MODES.MANUAL) {
    return applyManualDrawingOrder(files, cfg.fileOrder, opts);
  }
  return sortDrawingsAutomatic(files, opts);
}

export function moveDrawingOrderId(ids, fileId, direction) {
  const list = normalizeDrawingFileOrder(ids);
  const id = safeStr(fileId);
  const ix = list.indexOf(id);
  if (ix < 0) return list;
  const delta = lower(direction) === "up" ? -1 : lower(direction) === "down" ? 1 : 0;
  const j = ix + delta;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[ix], next[j]] = [next[j], next[ix]];
  return next;
}

export function reorderDrawingOrderIds(ids, fromIndex, toIndex) {
  const list = normalizeDrawingFileOrder(ids);
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length ||
    fromIndex === toIndex
  ) {
    return list;
  }
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

/**
 * Build Firestore patch for saving / resetting order. Caller writes to pmProjects only.
 * Does not touch shares, reviewRequests, or file docs.
 */
export function buildDrawingOrderSavePatch({ mode, fileOrder, actorUid = null } = {}) {
  const normalizedMode = normalizeDrawingOrderMode(mode);
  if (normalizedMode === DRAWING_ORDER_MODES.AUTOMATIC) {
    return {
      drawingOrderMode: DRAWING_ORDER_MODES.AUTOMATIC,
      drawingFileOrder: [],
      drawingOrderUpdatedAt: new Date().toISOString(),
      drawingOrderUpdatedByUid: actorUid || null,
    };
  }
  return {
    drawingOrderMode: DRAWING_ORDER_MODES.MANUAL,
    drawingFileOrder: normalizeDrawingFileOrder(fileOrder),
    drawingOrderUpdatedAt: new Date().toISOString(),
    drawingOrderUpdatedByUid: actorUid || null,
  };
}

/** Sort admin review rows' `.file` by canonical drawing order (stable on row order otherwise). */
export function sortReviewRowsByDrawingOrder(rows, projectOrConfig = null) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const files = list.map((r) => r?.file || { id: r?.fileId, name: r?.fileName || r?.name });
  const sortedFiles = sortDrawingsCanonical(files, projectOrConfig, { includeExcluded: true });
  const rank = new Map(sortedFiles.map((f, i) => [safeStr(f.id), i]));
  return list.sort((a, b) => {
    const ai = rank.get(safeStr(a?.file?.id || a?.fileId));
    const bi = rank.get(safeStr(b?.file?.id || b?.fileId));
    const aRank = ai == null ? Number.MAX_SAFE_INTEGER : ai;
    const bRank = bi == null ? Number.MAX_SAFE_INTEGER : bi;
    if (aRank !== bRank) return aRank - bRank;
    return safeStr(a?.fileId).localeCompare(safeStr(b?.fileId));
  });
}
